import React from "react";
import dayjs from "dayjs";
import { GET_PRODUCTIVITY } from "./graphql";
import { Card, Kpi, ReportState, SortTable, PersonButton, useReport, fmt, rupees, clockOf } from "./ui";

// Output per professional: bookings and revenue per working day, shift use
// and idle time.

const Util = ({ v }) => {
  if (v === null || v === undefined) return <span className="text-gray-300">—</span>;
  const cls = v < 40 ? "text-red-600" : v < 60 ? "text-amber-600" : "text-emerald-600";
  return (
    <span className="inline-flex items-center gap-2">
      <span className="w-14 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <span className="block h-full bg-[#FBB033]" style={{ width: `${Math.min(100, v)}%` }} />
      </span>
      <span className={cls}>{v}%</span>
    </span>
  );
};

const DayBars = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.completed));
  return (
    <div className="flex items-end gap-1 h-28">
      {rows.map((r) => (
        <div key={r.day} className="flex-1 min-w-[6px] flex flex-col items-center justify-end h-full" title={`${dayjs(r.day).format("ddd D MMM")}: ${r.completed} bookings by ${r.professionals} professionals, ${rupees(r.revenue)}`}>
          <div className="w-full rounded-t bg-[#FBB033]" style={{ height: `${(r.completed / max) * 100}%` }} />
        </div>
      ))}
    </div>
  );
};

const ProductivityTab = ({ filter, onPick }) => {
  const { data, loading, error } = useReport(GET_PRODUCTIVITY, "getProductivityReport", filter);
  const t = data?.team || {};

  const columns = [
    { key: "completed", label: "Done", title: "Completed bookings" },
    { key: "workingDays", label: "Days", title: "Days present or with a completed booking", cell: (e) => fmt(e.workingDays) },
    { key: "idleDays", label: "Idle days", title: "Present but no completed booking", cell: (e) => (e.idleDays ? <span className="text-amber-600">{e.idleDays}</span> : <span className="text-gray-300">0</span>) },
    { key: "perDay", label: "Per day", title: "Completed bookings per working day", cell: (e) => fmt(e.perDay, 1) },
    { key: "revenue", label: "Revenue", cell: (e) => rupees(e.revenue) },
    { key: "revenuePerDay", label: "Rev / day", cell: (e) => rupees(e.revenuePerDay) },
    { key: "avgTicket", label: "Avg ticket", cell: (e) => rupees(e.avgTicket) },
    { key: "commission", label: "Commission", cell: (e) => rupees(e.commission) },
    { key: "utilization", label: "Shift used", title: "Service hours ÷ shift hours on working days", cell: (e) => <Util v={e.utilization} /> },
    { key: "serviceHours", label: "Service hrs", cell: (e) => fmt(e.serviceHours, 1) },
    { key: "shiftHours", label: "Shift", title: "Hours between their first and last slot (12 h if not set)", cell: (e) => `${fmt(e.shiftHours, 1)} h` },
    { key: "medianGap", label: "Gap", title: "Median minutes between finishing one booking and starting the next, same day", cell: (e) => (e.medianGap === null ? "—" : `${e.medianGap} min`) },
    { key: "longGaps", label: "3h+ gaps", title: "Gaps over 3 hours between bookings in a day", cell: (e) => (e.longGaps ? <span className="text-amber-600">{e.longGaps}</span> : <span className="text-gray-300">0</span>) },
    { key: "medianWorkSpan", label: "Day span", title: "Median hours from first Start to last Finish", cell: (e) => (e.medianWorkSpan === null ? "—" : `${fmt(e.medianWorkSpan, 1)} h`) },
    { key: "medianStartMinute", label: "First start", title: "Typical time of day services start", cell: (e) => clockOf(e.medianStartMinute) },
  ];

  return (
    <ReportState loading={loading} error={error} data={data}>
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi label="Completed" value={fmt(t.completed)} sub={`by ${t.professionals} professionals`} />
            <Kpi label="Revenue" value={rupees(t.revenue)} sub={`${rupees(t.commission)} commission`} />
            <Kpi label="Per working day" value={fmt(t.perDay, 1)} sub="bookings per professional" />
            <Kpi label="Revenue / day" value={rupees(t.revenuePerDay)} sub="per professional per working day" />
            <Kpi label="Idle days" value={fmt(t.idleDays)} valueClass={t.idleDays ? "text-amber-600" : "text-gray-900"} sub="present but no booking done" />
          </div>

          {data.byDay.length > 1 && (
            <Card title="Bookings done per day" hint="Hover a bar for the day's numbers.">
              <DayBars rows={data.byDay} />
              <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                <span>{dayjs(data.byDay[0].day).format("D MMM")}</span>
                <span>{dayjs(data.byDay[data.byDay.length - 1].day).format("D MMM")}</span>
              </div>
            </Card>
          )}

          <Card title="By professional" hint="Working day = marked present or completed a booking. Service time uses Start–Finish, or the booked minutes when the buttons weren't pressed.">
            <SortTable
              columns={columns}
              rows={data.employees}
              initialSort={{ key: "revenue", dir: -1 }}
              rowKey={(e) => e.employeeId}
              nameCell={{ label: "Professional", cell: (e) => <PersonButton row={e} onPick={onPick} /> }}
            />
          </Card>
        </>
      )}
    </ReportState>
  );
};

export default ProductivityTab;
